import { S } from './state.js';
import { Audio } from './audio.js';
import { BAL } from './config.js';
import { GAME_VERSION, accountLevel } from './data/progression.js';
import { WEAPONS, evoEffectStr } from './data/weapons.js';
import { keys } from './main.js';
import { persist } from './economy.js';
import { checkAchievements, checkUnlocks } from './achievements.js';
import { updateCompanion, updateGemini, updateMirror } from './allies.js';
import { cam } from './meter.js';
import { player } from './entities.js';
import { clampToWorld, pushOutOfRocks, updateGroundFires, updateHazards } from './world.js';
import { JOY_SAT, joy } from './input.js';
import { chainLightning, collectCoin, popText, spawnParticle } from './procs.js';
import { collectPickup, openShrineDraft, updateFervor } from './shrines.js';
import { autoResolveMenus, ensureWorld } from './shop.js';
import { applyBurn, damageEnemy, playerShoot, updateCombat } from './combat.js';
import { submitRun, submitScore } from './ui.js';
// ---- Physics step (top-down twin-stick) ----
export function update() {
  if (S.paused || S.shopOpen || S.showJournal || S.showTalents || S.bloodConfirm || S.pandoraConfirm || S.bossReward || S.levelUp || S.shrineDraft) { autoResolveMenus(); return; }   // freeze the sim while any menu / shrine prompt is open — auto-shop may resolve the reward menus
  S.frame++;
  S.procThisFrame = 0;
  S.healThisFrame = 0; S.shieldHaste = 0;                  // reset the per-frame combat-leech heal cap + shield-haste budget
  S._sfxCoin = 5; S._sfxHit = 5; S._sfxShoot = 2;            // per-frame budgets for high-frequency sounds
  if (!S.started || S.dead) { ensureWorld(); return; }

  S.survival++;
  if (S.coinsRun > S.maxCoinsRun) S.maxCoinsRun = S.coinsRun;   // peak wealth → Gambler's Shrine prices off this
  checkAchievements(false);   // instant pops for mid-run milestones (wave, fervor, build, items, …)
  updateFervor();   // momentum decays each frame and refreshes its multipliers

  // weapon-evolution fanfare — announce once whenever the held weapon's evolved form comes online
  const _ek = S.EVO ? WEAPONS[S.weapon].key + '>' + S.EVO.name : '';
  if (_ek !== S.evoAnnounced) {
    S.evoAnnounced = _ek;
    if (_ek) {
      S.waveBanner = { text: '⤴ ' + WEAPONS[S.weapon].name.toUpperCase() + ' → ' + S.EVO.name.toUpperCase(), life: 2.8 };
      popText({ x: player.x, y: player.y - 44 }, S.EVO.desc + '  ·  ' + evoEffectStr(S.EVO));   // say WHAT it does, right at the moment
      Audio.play('cascade'); Audio.play('powerup');
      S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.08 : 0.22);
      for (let k = 0; k < 26; k++) spawnParticle(player.x, player.y, 'gold');
    }
  }

  // dash recharge (independent of movement)
  if (player.dashStock < S.DASH_CHARGES && --player.dashRe <= 0) { player.dashStock++; player.dashRe = S.DASH_RECHARGE; }

  // affix statuses: Venomous poison bleeds through shields & i-frames; Chilling slow saps your top speed
  if (player.poisonT > 0) {
    player.poisonT--; player.hp -= player.poison / 60;
    if (S.frame % 8 === 0) spawnParticle(player.x, player.y, 'bounce');
    if (player.poisonT <= 0) player.poison = 0;
    if (player.hp <= 0) { S.lastHitBy = 'venom (poison)'; S.lastHitDmg = 0; die(); return; }   // a tick, not a blow — recap shows just the cause
  }
  if (player.slowT > 0) player.slowT--; else player.slowAmt = 0;

  const slowF = player.slowT > 0 ? (1 - player.slowAmt) : 1;
  const moveCap = Math.min(13, S.MAX_SPEED * 0.32 * S.fvSpeed) * slowF;   // Fervor adds top speed; Chilling drags it down
  if (player.dashing > 0) {                           // dash burst: coast on the kick, heavy momentum
    player.dashing--;
    player.x += player.vx; player.y += player.vy;
    player.vx *= 0.86; player.vy *= 0.86;
    if (S.items.scorch && S.frame % 3 === 0)              // Scorch: the dash paints burning ground — movement IS the weapon
      S.groundFires.push({ x: player.x, y: player.y, r: (46 + S.items.scorch * 8) * S.RADIUS_MUL * S.GROUNDFIRE_MUL, life: 150 + S.items.scorch * 30, src: 'scorch' });
  } else {
    // WASD movement: accelerate toward the input direction, friction, speed cap
    let ax = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
    let ay = (keys.down ? 1 : 0) - (keys.up ? 1 : 0);
    if (joy.id !== null) { ax = joy.dx / JOY_SAT; ay = joy.dy / JOY_SAT; }   // touch stick overrides keys — ANALOG below saturation (walking is a real input)
    const al = Math.hypot(ax, ay);
    if (al > 1) { ax /= al; ay /= al; }               // normalize only past unit so keyboard diagonals aren't faster but a half-tilted stick still walks
    player.vx += ax * moveCap * 0.35;
    player.vy += ay * moveCap * 0.35;
    player.vx *= 0.8; player.vy *= 0.8;               // friction → snappy stop
    const sp = Math.hypot(player.vx, player.vy);
    if (sp > moveCap) { player.vx *= moveCap / sp; player.vy *= moveCap / sp; }
    player.x += player.vx; player.y += player.vy;
  }

  // boss arena: you're walled inside the ring
  if (S.arena.active) {
    const adx = player.x - S.arena.x, ady = player.y - S.arena.y, ad = Math.hypot(adx, ady), lim = S.arena.r - player.r;
    if (ad > lim) { player.x = S.arena.x + adx / ad * lim; player.y = S.arena.y + ady / ad * lim; player.vx *= 0.4; player.vy *= 0.4; }
  }
  // world walls (0.16): the void has an edge — running is a tactic again, not a build
  {
    pushOutOfRocks(player, player.r);   // terrain blocks bodies (slide along), never bullets
    if (clampToWorld(player, player.r)) { player.vx *= 0.4; player.vy *= 0.4; }
  }

  // dynamic zoom: ease out a touch at speed so fast play stays readable; on a boss-arena seal, PUNCH in then ease back
  const spd = Math.hypot(player.vx, player.vy);
  let targetZoom = 1 - Math.min(0.16, (spd / 10) * 0.16), zlerp = 0.06;
  if (S.bossIntro) { if (--S.bossIntro.t <= 0) S.bossIntro = null; else { targetZoom = 1.12; zlerp = 0.18; } }   // intro beat owns the camera briefly
  S.zoom += (targetZoom - S.zoom) * zlerp;

  // camera centers on the player
  cam.x += ((player.x - S.W / 2) - cam.x) * 0.14;
  cam.y += ((player.y - S.H / 2) - cam.y) * 0.14;

  // stage-2 mechanic ticks: Gatling spin-up (hold to accelerate) + Tempest auto-pulse (surrounded → lightning)
  if (S.EVO2 && S.EVO2.spin) S.spinup = (S.holding || S.autoFiring) ? Math.min(1, S.spinup + 1 / 90) : Math.max(0, S.spinup - 0.08);   // auto-aim keeps the barrels spinning too
  else S.spinup = 0;
  S.stillT = Math.hypot(player.vx, player.vy) < 0.6 ? S.stillT + 1 : 0;   // Siege Mode: planted artillery
  S.spinHeat = (S.holding || S.autoFiring) ? Math.min(1, S.spinHeat + 1 / 100) : Math.max(0, S.spinHeat - 0.06);   // 0.22 kinetic Spin-up heat (Flywheel/Suppressing Fire/Gatling) — warms up while firing, bleeds when you stop
  S.momentum = Math.hypot(player.vx, player.vy) > 5 ? Math.min(1, S.momentum + 1 / 120) : Math.max(0, S.momentum - 0.02);   // 0.22 tank Momentum: sustained movement builds the charge (Unstoppable doubles the ram bite)
  if (S.EVO2 && S.EVO2.tempest && --S.tempestT <= 0) {
    let near = 0;
    for (const e of S.enemies) { if (!e.dead && Math.hypot(e.x - player.x, e.y - player.y) < 320 && ++near >= 3) break; }
    if (near >= 3) { chainLightning(player.x, player.y, S.DAMAGE * 0.75 * 0.5, 3, 1, 'arc', 0.85); S.tempestT = 30; }
    else S.tempestT = 10;
  }
  playerShoot();   // hold mouse to fire toward the cursor
  if (S.companion) updateCompanion();
  updateGemini(); updateMirror();   // mythic allies
  ensureWorld();
  updateHazards();
  updateGroundFires();
  Audio.music(Math.min(8, S.enemies.length / 4 + S.waveNum / 5));

  // coins home in once within the (generous) magnet field
  const magR = S.MAGNET + 40;
  for (const c of S.coins) {
    if (c.got) continue;
    let dx = player.x - c.x, dy = player.y - c.y;
    let d = Math.hypot(dx, dy) || 0.0001;
    if (!c.mag && d < magR) c.mag = true;
    if (c.mag) {
      c.spd = (c.spd || 5) + 1.4;
      const step = Math.min(d, c.spd);
      c.x += (dx / d) * step; c.y += (dy / d) * step;
      dx = player.x - c.x; dy = player.y - c.y; d = Math.hypot(dx, dy);
      if (S.items.sharpcoins && S.enemies.length && (c.dmgCd = (c.dmgCd || 0) - 1) <= 0) {   // Sharp Coins (0.22 Coinstorm path): a magnetized coin slashes foes it flies past — the pickup phase becomes a weapon
        for (const e of S.enemies) {
          if (e.dead || Math.hypot(e.x - c.x, e.y - c.y) >= e.r + 10) continue;
          const cv = Math.max(1, Math.round(S.GREED * S.fvGreed * (c.worth || 1)));   // bigger coins cut deeper — a consolidated (fat) coin slashes for its full denomination, so capping entity count doesn't nerf Sharp Coins
          damageEnemy(e, (8 + S.DAMAGE * 0.12) * (0.6 + 0.4 * cv) * S.items.sharpcoins, 1, 'coinstorm');
          c.dmgCd = 8; break;
        }
      }
    }
    if (d < player.r + 14) collectCoin(c, 0);
  }
  S.coins = S.coins.filter(c => !c.got);
  // hard field cap (perf backstop): if a kill-burst outruns collection, silently bank the OLDEST surplus —
  // value is kept (added straight to the run total), entities are bounded so the arena can't carpet & tank FPS.
  const fieldMax = BAL.econ.coinFieldMax;
  if (fieldMax && S.coins.length > fieldMax) {
    const over = S.coins.length - fieldMax;
    const unit = Math.max(1, Math.round(S.GREED * S.fvGreed));
    let banked = 0;
    for (let i = 0; i < over; i++) banked += Math.round(unit * (S.coins[i].worth || 1));
    S.coins.splice(0, over);
    S.coinsRun += banked; S.coinsEarned += banked;
  }
  if (S.items.coincyclone && S.enemies.length) {   // Coin Cyclone (0.22 Coinstorm path): the inrush of coins forms a shredding vortex — radius & bite grow with coins in flight
    let flying = 0; for (const c of S.coins) if (c.mag) flying++;
    if (flying > 0) {
      const cr = (50 + 30 * S.items.coincyclone + 6 * Math.min(20, flying)) * S.RADIUS_MUL;
      const cyd = (4 + S.DAMAGE * 0.06) * (0.6 + 0.4 * S.GREED) / 60 * Math.min(20, flying) * 0.25;
      for (const e of S.enemies) if (!e.dead && Math.hypot(e.x - player.x, e.y - player.y) < cr) damageEnemy(e, cyd, 1, 'coinstorm');
    }
  }

  // power pickups home in the same way, but collect into heals / run-damage
  for (const p of S.pickups) {
    if (p.got) continue;
    if (p.life !== undefined && --p.life <= 0) { p.got = true; continue; }   // despawn old drops (guns, and now hearts/shards — they carpeted the arena & tanked FPS on god-builds)
    if (p.kind === 'weapon') continue;   // guns are taken with E, not magnet-collected
    let dx = player.x - p.x, dy = player.y - p.y, d = Math.hypot(dx, dy) || 0.0001;
    if (!p.mag && d < magR) p.mag = true;
    if (p.mag) {
      p.spd = (p.spd || 4) + 1.2;
      const s = Math.min(d, p.spd);
      p.x += (dx / d) * s; p.y += (dy / d) * s;
      dx = player.x - p.x; dy = player.y - p.y; d = Math.hypot(dx, dy);
    }
    if (d < player.r + 16) collectPickup(p);
  }
  S.pickups = S.pickups.filter(p => !p.got);

  // Item Caches auto-open on walk-in (0.14, player feedback: too much E-pressing). Browsing is free —
  // the sim freezes, you buy or LEAVE. s.snooze: after LEAVE the cache won't reopen until you step away.
  // Blood / gamble / pandora / weapon swaps stay deliberate (E) — those are destructive or costly.
  for (const s of S.shrines) {
    if (s.taken || s.kind !== 'item') continue;
    const sd = Math.hypot(s.x - player.x, s.y - player.y);
    if (s.snooze) { if (sd > player.r + 96) s.snooze = false; continue; }
    if (sd < player.r + 56) { openShrineDraft(s); break; }
  }

  updateCombat();   // waves, enemies, projectiles, player bullets, regen

  // trail
  player.trail.push({ x: player.x, y: player.y });
  while (player.trail.length > 14) player.trail.shift();

  // particles + floating text (hard caps keep epic cascades from tanking the frame rate)
  for (const p of S.particles) { p.x += p.vx; p.y += p.vy; p.vy += 0.12; p.life -= 0.025; }
  S.particles = S.particles.filter(p => p.life > 0);
  if (S.particles.length > 440) S.particles.splice(0, S.particles.length - 440);
  for (const t of S.texts) { t.y -= 0.8; t.life -= 0.015; }
  S.texts = S.texts.filter(t => t.life > 0);
  if (S.texts.length > 50) S.texts.splice(0, S.texts.length - 50);
  for (const dt of S.dmgTexts) { dt.y -= 1.0; dt.life -= 0.03; }
  S.dmgTexts = S.dmgTexts.filter(dt => dt.life > 0);

  // proc effect visuals + combo timer
  for (const e of S.effects) {
    e.life -= (e.type === 'spark') ? 0.08 : 0.05;
    if (e.type === 'shock') e.r += (e.maxR - e.r) * 0.25;
    if (e.followPlayer) { e.x = player.x; e.y = player.y; }   // slams that ARE the player's swing ride along (fast movement made them read off-center)
  }
  S.effects = S.effects.filter(e => e.life > 0);
  if (S.effects.length > 150) S.effects.splice(0, S.effects.length - 150);
  if (S.chainTimer > 0 && --S.chainTimer === 0) S.chainCount = 0;

  if (S.shake > 0) S.shake *= 0.86;
  // death now happens only via HP (hurtPlayer → die)
}

export function die() {
  if (S.dead) return;
  if (S.items.phoenix && !S.phoenixUsed) {   // Phoenix Clause (mythic): refuse death ONCE — detonate, ignite everything, rise at 40%
    S.phoenixUsed = true;
    player.hp = S.MAX_HP * 0.4; player.iFrames = 150;
    player.recentDmg = 0;   // clean mercy slate — the death burst's tally would otherwise gift free shaving post-revive
    S.waveBanner = { text: '🪦 PHOENIX CLAUSE — DEATH REFUSED', life: 2.8 };
    S.effects.push({ type: 'shock', x: player.x, y: player.y, r: 12, maxR: 560, life: 1 });
    for (const e of S.enemies) {
      if (e.dead) continue;
      damageEnemy(e, e.maxHp * (e.kind === 'boss' ? 0.1 : 0.5), 1, 'phoenix');
      if (!e.dead) applyBurn(e, S.BURN_DPS * 2, 240);
    }
    S.shake = 18; S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.15 : 0.5);
    Audio.play('cascade'); Audio.play('powerup');
    for (let i = 0; i < 50; i++) spawnParticle(player.x, player.y, 'gold');
    return;
  }
  S.dead = true; S.deathAt = Date.now();
  S.best = Math.max(S.best, S.waveNum);   // best = furthest wave reached
  S.save.best = S.best;
  S.save.money += S.coinsRun;   // bank everything collected this run
  S.save.kills += S.killsRun;   // lifetime totals feed the unlock milestones
  S.save.coinsTotal += S.coinsRun;
  S.save.runs++;
  // META-XP (0.25): paced by depth (wave), effort (kills) and survival — leveling buys relic SLOTS, not power
  S.save.timeTotal = (S.save.timeTotal || 0) + Math.round(S.survival / 60);   // lifetime seconds survived
  const _metaXp = Math.round(S.waveNum * 60 + S.killsRun * 0.5 + (S.survival / 60) * 2);
  const _lvlBefore = accountLevel(S.save.accountXp || 0);
  S.save.accountXp = (S.save.accountXp || 0) + _metaXp;
  S.lastRunXp = _metaXp;
  S.pendingAccountLevels = accountLevel(S.save.accountXp) - _lvlBefore;
  // record this run on the local leaderboard (ranked by wave, then kills, then time)
  const entry = { wave: S.waveNum, kills: S.killsRun, time: S.survival, coins: S.coinsRun, weapon: WEAPONS[S.weapon].key, v: GAME_VERSION, t: Date.now() };
  if (!Array.isArray(S.save.scores)) S.save.scores = [];
  S.save.scores.push(entry);
  S.save.scores.sort((a, b) => (b.wave - a.wave) || (b.kills - a.kills) || (b.time - a.time));
  S.save.scores = S.save.scores.slice(0, 10);
  S.lastScoreT = entry.t;
  submitScore(entry);   // push to the global board (no-op/ignored if the server isn't reachable)
  submitRun();          // log the full run for balance telemetry (runs.jsonl)
  S.pendingUnlocks = [];
  checkUnlocks(false);      // award any milestone reached this run (with fanfare)
  checkAchievements(false); // award end-of-run achievements (final wave / kills / coins / runs)
  persist();
  S.shake = 14; S.flash = Math.max(S.flash, 0.5);
  Audio.play('death');
  for (let i = 0; i < 30; i++) spawnParticle(player.x, player.y, 'normal');
}
